<template>
    <!-- the team's logo first, then its five on court, across the screen up to the right edge -->
    <div class="live-card active-players rounded-lg">
        <div class="active-players__logo">
            <img :src="`${assetsUrl}/${displayedTeam?.logo}`" alt="" class="live-logo live-logo--lg">
        </div>

        <!-- players for the currently visible team -->
        <div v-for="player in displayedPlayers" :key="player.id" class="active-players__player">
            <div class="active-players__photo">
                <img :src="`${assetsUrl}/${player.image}`" alt="" @error="$event.target.src = defaultPlayerImg">
            </div>
            <div v-fit-text="{ max: 1.15, min: 0.8, lines: 2 }" class="active-players__name">
                {{ jerseyLabel(player) }}{{ player.name }}
            </div>
            <div class="active-players__points font-weight-bold">
                {{ player.stats[0]?.points ?? 0 }} <span class="custom-teal-text">points</span>
            </div>
            <div class="active-players__fouls">
                <span class="custom-teal-text font-weight-bold mr-1">fouls</span>
                <v-icon v-for="i in foulsCount(player)" :key="`i-${i}`" small :color="foulsCount(player) >= 5 ? 'red' : 'teal'">mdi-minus-thick</v-icon>
                <v-icon v-for="j in remainingFouls(player)" :key="`j-${j}`" small color="white">mdi-minus-thick</v-icon>
            </div>
        </div>
    </div>
</template>

<script>
    import matchService from '@/services/match'
    import { foulsOf } from '@/utils/teamStats'
    import fitText from '@/utils/fitText'
    export default {
        directives: { fitText },
        props: {
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                match: null,
                activePlayers: {
                    team1: [],
                    team2: []
                },
                // rotation state
                visibleTeamIndex: 1, // 1 => team1, 2 => team2
                activeTimer: null,
                rotateIntervalMs: 10000,
                rotationStage: 0 // renamed from _rotationStage
            }
        },
        computed: {
            displayedPlayers() {
                return this.visibleTeamIndex === 1 ? this.activePlayers.team1 : this.activePlayers.team2
            },
            displayedTeam() {
                return this.visibleTeamIndex === 1 ? this.match?.team1 : this.match?.team2
            }
        },
        mounted() {
            this.fetchMatch(this.$route.params.id).then((resp) => {
                this.match = resp
                this.getActivePlayers().then(() => {
                    this.startRotation()
                })
            })
        },
        beforeDestroy() {
            this.stopRotation()
        },
        methods: {
            fetchMatch(matchId) {
                return matchService.getMatch(matchId).then(resp => {
                    return resp.data
                })
            },
            async getActivePlayers() {
                try {
                    const resp = await matchService.getActivePlayers(this.$route.params.id)
                    // reset arrays to avoid duplicates on repeated calls
                    this.activePlayers.team1 = []
                    this.activePlayers.team2 = []

                    resp.data.team1Players.forEach(playerRef => {
                        const p = this.match.team1.players.find(p => p.id === playerRef.playerId)
                        if (p) this.activePlayers.team1.push(p)
                    })
                    resp.data.team2Players.forEach(playerRef => {
                        const p = this.match.team2.players.find(p => p.id === playerRef.playerId)
                        if (p) this.activePlayers.team2.push(p)
                    })
                } catch (e) {
                    console.error('Failed to load active players', e)
                }
            },
            startRotation() {
                this.stopRotation()
                this.rotationStage = 0
                // show team1 first
                this.visibleTeamIndex = 1
                this.rotationStage = 1

                // after rotateIntervalMs show team2
                this.activeTimer = setTimeout(() => {
                    this.visibleTeamIndex = 2
                    this.rotationStage = 2

                    // after another rotateIntervalMs emit and stop
                    this.activeTimer = setTimeout(() => {
                        // finished showing both teams once
                        this.$emit('setShowScreen')
                        this.stopRotation()
                    }, this.rotateIntervalMs)
                }, this.rotateIntervalMs)
            },
            stopRotation() {
                if (this.activeTimer) {
                    clearTimeout(this.activeTimer)
                    this.activeTimer = null
                }
            },
            // "#7 " before the name; nothing for a player without a number
            jerseyLabel(player) {
                const jersey = player.alternativeJersey ?? player.jersey
                return jersey !== null && jersey !== undefined ? `#${jersey} ` : ''
            },
            foulsCount(player) {
                // every foul of the player: personal, technical and unsportsmanlike
                const total = foulsOf(player?.stats?.[0] || {})
                return Math.min(5, Math.max(0, total))
            },
            remainingFouls(player) {
                const rem = 5 - this.foulsCount(player)
                return rem > 0 ? rem : 0
            }
        }
    }
</script>

<style>
    .v-progress-circular__info {
        display: block;
    }

    .icon-outline {
        text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px  1px 0 #000,
            1px  1px 0 #000;
    }

    .circular-border {
        display: inline-flex;
        border: 2px solid #fff;     /* white border */
        border-radius: 50%;         /* make it circular */
        padding: 2px;               /* space between border and ring */
    }

    .player-line {
        display: flex;
        align-items: center;
    }
</style>

<style scoped>
    /* the full width left after the scoreboard, at the scoreboard's height */
    .active-players {
        display: flex;
        align-items: stretch;
        width: 100%;
        padding: 0 8px;
        font-family: Arial, Helvetica, sans-serif;
    }

    .active-players__logo {
        display: flex;
        flex: 0 0 170px;
        align-items: center;
        justify-content: center;
    }

    /* the five share the rest equally, whatever their names */
    .active-players__player {
        display: flex;
        flex: 1 1 0;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        min-width: 0;
        padding: 10px 6px;
        text-align: center;
    }

    .active-players__photo {
        display: flex;
        justify-content: center;
        height: 64px;
    }

    .active-players__photo img {
        height: 100%;
        width: auto;
        max-width: 100%;
        object-fit: contain;
    }

    /* never cut: on two lines (between words), set smaller if it still does not fit */
    .active-players__name {
        width: 100%;
        line-height: 1.15;
    }

    .active-players__points {
        font-size: 1.4rem;
        line-height: 1.2;
    }

    .active-players__fouls {
        font-size: 1rem;
        white-space: nowrap;
    }
</style>
